import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toPng, toJpeg } from "html-to-image";
import { X, Download, Share2, Image as ImageIcon, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";

// ---- palettes (mirror mobile src/social/palette.ts) ----
const PALETTES = {
  cream: { bg: "#F4ECD8", panel: "rgba(9,35,29,0.86)", ink: "#F4ECD8", head: "#9CC24A", accent: "#E6B733", sub: "#CFE0C2", line: "rgba(244,236,216,0.22)", scrim: "rgba(9,35,29,0.10)", chip: "#103B31", chipInk: "#F4ECD8", good: "#9CC24A" },
  green: { bg: "#103B31", panel: "rgba(6,26,22,0.80)", ink: "#F4ECD8", head: "#9CC24A", accent: "#E6B733", sub: "#CFE0C2", line: "rgba(244,236,216,0.22)", scrim: "rgba(6,26,22,0.18)", chip: "#E6B733", chipInk: "#0B2B25", good: "#9CC24A" },
  dark: { bg: "#0E241F", panel: "rgba(4,16,14,0.82)", ink: "#F4ECD8", head: "#7ED957", accent: "#2E7DFF", sub: "#B9C7BD", line: "rgba(244,236,216,0.18)", scrim: "rgba(0,0,0,0.22)", chip: "#2E7DFF", chipInk: "#FFFFFF", good: "#7ED957" },
};
const MASCOTS = {
  pencil_golfer_club: "/cadii/mascots/pencil_golfer_club.png",
  pencil_golfer_swing: "/cadii/mascots/pencil_golfer_swing.png",
  golfball_celebrate: "/cadii/mascots/golfball_celebrate.png",
  scorecard_writing: "/cadii/mascots/scorecard_writing.png",
};
const TYPE_META = { round: "Ronda", scorecard: "Tarjeta", hole: "Hoyo", match: "Match" };
const STYLE_LABEL = { cream: "Crema", green: "Verde", dark: "Oscuro" };
const POS_LABEL = { top: "Arriba", center: "Centro", bottom: "Abajo" };
const POSITION_TEMPLATES = ["round_recap_01", "tv_score_01", "match_winner_01"];

function labelForDiff(d) {
  if (d <= -3) return "ALBATROSS";
  if (d === -2) return "EAGLE";
  if (d === -1) return "BIRDIE";
  if (d === 0) return "PAR";
  if (d === 1) return "BOGEY";
  if (d === 2) return "DOUBLE BOGEY";
  return `+${d}`;
}
const fmtToPar = (v) => (v == null || v === 0 ? "E" : v > 0 ? `+${v}` : `${v}`);

function buildData(payload, playerId, hole) {
  const st = payload.player_stats[playerId] || payload.player_stats[payload.default_player_id];
  const player = payload.players.find((p) => p.player_id === playerId);
  const holes = st?.holes || [];
  let holeInfo = null;
  if (hole != null) {
    const h = holes.find((x) => x.hole === hole);
    if (h && h.score != null) holeInfo = { hole: h.hole, par: h.par, toPar: h.to_par, label: labelForDiff(h.to_par) };
  }
  return {
    playerName: player?.name || "", course: payload.course || "", roundName: payload.name || "",
    score: st?.score || 0, toPar: st?.to_par || 0, handicap: st?.handicap ?? null, position: st?.position ?? null,
    birdies: st?.birdies || 0, eagles: st?.eagles || 0, bogeys: st?.bogeys || 0,
    holes, match: payload.match, holeInfo,
  };
}
function buildFreePayload(f) {
  return {
    round_id: "free", source: "v2", name: f.course || "Mi Golf", course: f.course || "", club: null, status: "completed",
    players: [{ player_id: "me", name: f.name || "Jugador" }],
    default_player_id: "me",
    player_stats: {
      me: {
        score: parseInt(f.score) || 0, to_par: parseInt(f.toPar) || 0,
        birdies: 0, eagles: 0, bogeys: 0, pars: 0, handicap: null, position: null, holes: [],
      },
    },
    match: { available: false },
    available_types: ["round"],
  };
}
function bestHole(payload, playerId) {
  const st = payload.player_stats[playerId];
  if (!st) return undefined;
  const played = st.holes.filter((h) => h.score != null);
  if (!played.length) return undefined;
  played.sort((a, b) => a.to_par - b.to_par || a.hole - b.hole);
  return played[0].hole;
}

// ---- templates (DOM overlays) ----
function Sig({ W, pal }) {
  return <span style={{ fontSize: W * 0.045, fontWeight: 900, fontStyle: "italic", color: pal.ink }}>cadii</span>;
}
function Panel({ W, pal, children, style }) {
  return <div style={{ background: pal.panel, borderRadius: W * 0.05, padding: W * 0.055, ...style }}>{children}</div>;
}
function Mascot({ src, W, style }) {
  return <img src={src} alt="" style={{ position: "absolute", width: W * 0.26, height: W * 0.26, objectFit: "contain", ...style }} />;
}

function TemplateOverlay({ id, W, H, data, pal, position, mascot, mascotName, show }) {
  const justify = position === "top" ? "flex-start" : position === "center" ? "center" : "flex-end";
  const src = MASCOTS[mascotName] || MASCOTS.golfball_celebrate;

  if (id === "round_recap_01") {
    const stats = [];
    if (show.stats) { stats.push(["BIRDIES", data.birdies]); if (data.eagles > 0) stats.push(["EAGLES", data.eagles]); stats.push(["BOGEYS", data.bogeys]); }
    if (show.handicap && data.handicap != null) stats.push(["HCP", data.handicap]);
    return (
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", justifyContent: justify, padding: W * 0.05 }}>
        <Panel W={W} pal={pal}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ color: pal.head, fontWeight: 900, letterSpacing: 2, fontSize: W * 0.045 }}>ROUND RECAP</span>
            <Sig W={W} pal={pal} />
          </div>
          <div style={{ color: pal.ink, fontWeight: 900, fontSize: W * 0.095, marginTop: W * 0.02 }}>{data.playerName}</div>
          <div style={{ color: pal.sub, fontSize: W * 0.04, marginTop: 2 }}>{data.course}</div>
          <div style={{ display: "flex", alignItems: "flex-end", marginTop: W * 0.04 }}>
            <span style={{ color: pal.ink, fontWeight: 900, fontSize: W * 0.24, lineHeight: 1 }}>{data.score || "—"}</span>
            <div style={{ marginLeft: W * 0.04, marginBottom: W * 0.02 }}>
              <div style={{ color: pal.good, fontWeight: 900, fontSize: W * 0.09 }}>{fmtToPar(data.toPar)}</div>
              <div style={{ color: pal.sub, fontSize: W * 0.032, letterSpacing: 1 }}>TO PAR</div>
            </div>
          </div>
          {stats.length > 0 && (
            <div style={{ display: "flex", marginTop: W * 0.04, borderTop: `1px solid ${pal.line}`, paddingTop: W * 0.035 }}>
              {stats.map(([k, v]) => (
                <div key={k} style={{ flex: 1, textAlign: "center" }}>
                  <div style={{ color: pal.ink, fontWeight: 900, fontSize: W * 0.06 }}>{v}</div>
                  <div style={{ color: pal.sub, fontSize: W * 0.028, letterSpacing: 1 }}>{k}</div>
                </div>
              ))}
            </div>
          )}
          <div style={{ color: pal.sub, fontSize: W * 0.03, marginTop: W * 0.04, letterSpacing: 1 }}>MADE WITH CADII</div>
        </Panel>
        {mascot && <Mascot src={src} W={W} style={{ right: W * 0.02, bottom: justify === "flex-end" ? H * 0.32 : W * 0.04 }} />}
      </div>
    );
  }

  if (id === "tv_score_01") {
    const pos = show.position && data.position != null ? `T${data.position}` : null;
    return (
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", justifyContent: justify, padding: H * 0.05 }}>
        <div style={{ padding: `0 ${W * 0.04}px` }}>
          <div style={{ display: "flex", alignItems: "center", background: pal.panel, borderRadius: W * 0.03, padding: `${W * 0.03}px ${W * 0.04}px` }}>
            {pos && <div style={{ background: pal.chip, borderRadius: W * 0.02, padding: `${W * 0.015}px ${W * 0.03}px`, marginRight: W * 0.035 }}><span style={{ color: pal.chipInk, fontWeight: 900, fontSize: W * 0.06 }}>{pos}</span></div>}
            <div style={{ flex: 1 }}>
              <div style={{ color: pal.ink, fontWeight: 900, fontSize: W * 0.058, letterSpacing: 1 }}>{data.playerName.toUpperCase()}</div>
              <div style={{ color: pal.sub, fontSize: W * 0.032, letterSpacing: 1 }}>{(data.course || data.roundName).toUpperCase()}</div>
            </div>
            <div style={{ margin: `0 ${W * 0.03}px` }}><span style={{ color: pal.good, fontWeight: 900, fontSize: W * 0.085 }}>{fmtToPar(data.toPar)}</span></div>
            <div style={{ background: pal.chip, borderRadius: W * 0.02, padding: `${W * 0.02}px ${W * 0.035}px` }}><span style={{ color: pal.chipInk, fontWeight: 900, fontSize: W * 0.075 }}>{data.score || "—"}</span></div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, padding: "0 4px" }}>
            <span style={{ color: "#F4ECD8", fontWeight: 900, fontStyle: "italic", fontSize: W * 0.04, textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}>cadii</span>
            <span style={{ color: "#F4ECD8", fontSize: W * 0.032, letterSpacing: 1, textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}>LIVE · {data.roundName.toUpperCase()}</span>
          </div>
        </div>
        {mascot && <Mascot src={src} W={W} style={{ right: W * 0.03, bottom: justify === "flex-end" ? H * 0.2 : undefined, top: justify === "flex-start" ? H * 0.18 : undefined, width: W * 0.22, height: W * 0.22 }} />}
      </div>
    );
  }

  if (id === "scorecard_01") {
    const cardW = W * 0.92; const cw = (cardW - W * 0.11) / 10;
    const front = data.holes.filter((h) => h.hole <= 9);
    const back = data.holes.filter((h) => h.hole >= 10);
    const out = front.reduce((s, h) => s + (h.score || 0), 0);
    const inn = back.reduce((s, h) => s + (h.score || 0), 0);
    const cell = (h) => {
      const d = h.to_par; let border = "none", color = pal.ink, br = 0;
      if (d == null) color = pal.sub;
      else if (d <= -1) { border = `${d <= -2 ? 2 : 1.5}px solid ${pal.good}`; color = pal.good; br = cw * 0.4; }
      else if (d >= 1) { border = `${d >= 2 ? 2 : 1.5}px solid ${pal.accent}`; color = pal.accent; }
      return (
        <div key={h.hole} style={{ width: cw, display: "flex", justifyContent: "center" }}>
          <div style={{ width: cw * 0.8, height: cw * 0.8, border, borderRadius: br, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ color, fontWeight: 900, fontSize: cw * 0.44 }}>{h.score ?? "·"}</span>
          </div>
        </div>
      );
    };
    const renderTable = (list, label) => (
      <div style={{ marginBottom: W * 0.03 }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ width: cw * 1.2, color: pal.sub, fontWeight: 800, fontSize: cw * 0.42 }}>{label}</span>
          {list.map((h) => <span key={h.hole} style={{ width: cw, textAlign: "center", color: pal.sub, fontSize: cw * 0.42 }}>{h.hole}</span>)}
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ width: cw * 1.2, color: pal.sub, fontWeight: 800, fontSize: cw * 0.42 }}>PAR</span>
          {list.map((h) => <span key={h.hole} style={{ width: cw, textAlign: "center", color: pal.sub, fontSize: cw * 0.42 }}>{h.par}</span>)}
        </div>
        <div style={{ display: "flex", alignItems: "center", marginTop: 2 }}>
          <span style={{ width: cw * 1.2, color: pal.ink, fontWeight: 900, fontSize: cw * 0.42 }}>SC</span>
          {list.map(cell)}
        </div>
      </div>
    );
    return (
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: W * 0.04 }}>
        <div style={{ width: cardW, background: pal.panel, borderRadius: W * 0.05, padding: W * 0.045 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ color: pal.ink, fontWeight: 900, fontSize: W * 0.055 }}>{data.playerName}</span>
            <Sig W={W} pal={pal} />
          </div>
          <div style={{ color: pal.sub, fontSize: W * 0.033, marginBottom: W * 0.03 }}>{data.course}</div>
          {renderTable(front, "OUT")}
          {back.length > 0 && renderTable(back, "IN")}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: `1px solid ${pal.line}`, marginTop: W * 0.02, paddingTop: W * 0.03 }}>
            <span style={{ color: pal.sub, fontSize: W * 0.035 }}>OUT {out || "—"} · IN {inn || "—"}</span>
            <span style={{ color: pal.ink, fontWeight: 900, fontSize: W * 0.05 }}>{data.score || "—"} <span style={{ color: pal.good }}>{fmtToPar(data.toPar)}</span></span>
          </div>
        </div>
      </div>
    );
  }

  if (id === "hole_result_01") {
    const info = data.holeInfo; const label = info?.label || "PAR"; const toPar = info?.toPar ?? 0;
    return (
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: W * 0.06 }}>
        <div style={{ width: W * 0.86, background: pal.panel, borderRadius: W * 0.06, padding: `${W * 0.08}px ${W * 0.07}px`, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <span style={{ color: pal.sub, fontWeight: 800, letterSpacing: 3, fontSize: W * 0.04 }}>HOLE {info?.hole ?? "—"} · PAR {info?.par ?? "—"}</span>
          <span style={{ color: pal.head, fontWeight: 900, fontStyle: "italic", fontSize: W * 0.2, margin: `${W * 0.02}px 0`, textAlign: "center" }}>{label}!</span>
          <div style={{ background: pal.chip, borderRadius: "50%", width: W * 0.26, height: W * 0.26, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ color: pal.chipInk, fontWeight: 900, fontSize: W * 0.11 }}>{fmtToPar(toPar)}</span>
          </div>
          <span style={{ color: pal.ink, fontWeight: 800, fontSize: W * 0.05, marginTop: W * 0.05 }}>{data.playerName}</span>
          <span style={{ marginTop: W * 0.02 }}><Sig W={W} pal={pal} /></span>
        </div>
        {mascot && <Mascot src={src} W={W} style={{ right: W * 0.04, bottom: H * 0.16, width: W * 0.32, height: W * 0.32 }} />}
      </div>
    );
  }

  // match_winner_01
  const m = data.match || {};
  return (
    <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", justifyContent: justify, padding: W * 0.05 }}>
      <Panel W={W} pal={pal}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ color: pal.head, fontWeight: 900, letterSpacing: 2, fontSize: W * 0.045 }}>MATCH WINNER</span>
          <Sig W={W} pal={pal} />
        </div>
        <div style={{ color: pal.ink, fontWeight: 900, fontSize: W * 0.085, marginTop: W * 0.025 }}>{m.winner_name || data.playerName}</div>
        {!!m.result && <div style={{ color: pal.accent, fontWeight: 900, fontSize: W * 0.16, margin: `${W * 0.01}px 0` }}>{m.result}</div>}
        <div style={{ color: pal.sub, fontSize: W * 0.04, marginTop: W * 0.02, letterSpacing: 1 }}>VS</div>
        <div style={{ color: pal.ink, fontWeight: 700, fontSize: W * 0.05 }}>{m.opponent || ""}</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: `1px solid ${pal.line}`, marginTop: W * 0.04, paddingTop: W * 0.03 }}>
          <span style={{ color: pal.sub, fontSize: W * 0.035 }}>{data.course}</span>
          <span style={{ color: pal.accent, fontWeight: 800, fontSize: W * 0.035, letterSpacing: 1 }}>{(m.match_type || "MATCH").toUpperCase()}</span>
        </div>
      </Panel>
      {mascot && <Mascot src={src} W={W} style={{ right: W * 0.03, bottom: justify === "flex-end" ? H * 0.3 : W * 0.04 }} />}
    </div>
  );
}

const chipCls = (active) =>
  `px-3.5 py-2 rounded-full text-sm font-bold border transition ${active ? "bg-[#103B31] text-white border-[#103B31]" : "bg-white text-[#6B7A6F] border-[#DED5C2]"}`;

export default function SocialStudio() {
  const { roundId } = useParams();
  const isFree = roundId === "free";
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const canvasRef = useRef(null);

  const [payload, setPayload] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [formats, setFormats] = useState({});
  const [loading, setLoading] = useState(true);

  const [contentType, setContentType] = useState(sp.get("type") || "round");
  const [playerId, setPlayerId] = useState("");
  const [templateId, setTemplateId] = useState(sp.get("template") || "");
  const [format, setFormat] = useState("story");
  const [style, setStyle] = useState("cream");
  const [position, setPosition] = useState("bottom");
  const [mascot, setMascot] = useState(true);
  const [toggles, setToggles] = useState(isFree ? { handicap: false, position: false, stats: false } : { handicap: true, position: true, stats: true });
  const [mediaUrl, setMediaUrl] = useState(null);
  const [hole, setHole] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const [freeForm, setFreeForm] = useState({ name: "", course: "", score: "", toPar: "" });

  useEffect(() => {
    if (isFree) {
      (async () => {
        try {
          const t = await fetch(`${API}/social/templates`, { credentials: "include" }).then((r) => r.json());
          setTemplates(t.templates); setFormats(t.formats); setPlayerId("me");
        } catch {
          toast.error("No se pudieron cargar las plantillas"); navigate("/studio");
        } finally { setLoading(false); }
      })();
      return;
    }
    (async () => {
      try {
        const [p, t] = await Promise.all([
          fetch(`${API}/social/rounds/${roundId}/payload`, { credentials: "include" }).then((r) => r.json()),
          fetch(`${API}/social/templates`, { credentials: "include" }).then((r) => r.json()),
        ]);
        if (p.detail) throw new Error(p.detail);
        setPayload(p); setTemplates(t.templates); setFormats(t.formats); setPlayerId(p.default_player_id);
      } catch {
        toast.error("No se pudo cargar la ronda");
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, [roundId, navigate, isFree]);

  useEffect(() => {
    if (isFree) setPayload(buildFreePayload(freeForm));
  }, [isFree, freeForm]);

  const availableTypes = useMemo(() => {
    if (!payload) return [];
    return ["round", "scorecard", "hole", "match"].filter(
      (t) => templates.some((tp) => tp.content_type === t) && payload.available_types.includes(t) &&
        (t !== "match" || payload.match.available) && (t !== "hole" || bestHole(payload, playerId || payload.default_player_id) != null),
    );
  }, [payload, templates, playerId]);

  const typeTemplates = useMemo(() => templates.filter((t) => t.content_type === contentType), [templates, contentType]);
  const selectedTemplate = useMemo(() => typeTemplates.find((t) => t.template_id === templateId) || typeTemplates[0], [typeTemplates, templateId]);

  useEffect(() => { if (payload && availableTypes.length && !availableTypes.includes(contentType)) setContentType(availableTypes[0]); }, [payload, availableTypes, contentType]);
  useEffect(() => { if (typeTemplates.length && !typeTemplates.some((t) => t.template_id === templateId)) setTemplateId(typeTemplates[0].template_id); }, [typeTemplates, templateId]);
  useEffect(() => { if (contentType === "hole" && payload && hole == null) setHole(bestHole(payload, playerId || payload.default_player_id)); }, [contentType, payload, playerId, hole]);
  useEffect(() => { if (selectedTemplate && !selectedTemplate.media && mediaUrl) setMediaUrl(null); }, [selectedTemplate, mediaUrl]);
  useEffect(() => { if (selectedTemplate && !selectedTemplate.formats.includes(format)) setFormat(selectedTemplate.formats[0]); }, [selectedTemplate, format]);

  const onUpload = (e) => {
    const f = e.target.files?.[0];
    if (f) setMediaUrl(URL.createObjectURL(f));
  };

  const exportImage = useCallback(async () => {
    if (!canvasRef.current) return null;
    const previewW = canvasRef.current.offsetWidth;
    const opts = { pixelRatio: 1080 / previewW, cacheBust: true };
    return mediaUrl ? toJpeg(canvasRef.current, { ...opts, quality: 0.95 }) : toPng(canvasRef.current, opts);
  }, [mediaUrl]);

  const track = (event) => fetch(`${API}/social/track`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ event, template_id: selectedTemplate?.template_id, content_type: contentType, round_id: roundId }) }).catch(() => {});

  const download = async () => {
    setBusy(true);
    try {
      const uri = await exportImage();
      if (!uri) return;
      const a = document.createElement("a");
      a.href = uri; a.download = `cadii_${contentType}_${roundId}.${mediaUrl ? "jpg" : "png"}`; a.click();
      track("content_saved");
      toast.success("Imagen descargada");
    } catch {
      toast.error("No se pudo generar la imagen");
    } finally { setBusy(false); }
  };

  const share = async () => {
    setBusy(true);
    try {
      const uri = await exportImage();
      if (!uri) return;
      const blob = await (await fetch(uri)).blob();
      const file = new File([blob], `cadii.${mediaUrl ? "jpg" : "png"}`, { type: blob.type });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "CADII" });
        track("content_shared");
      } else {
        await download();
      }
    } catch {
      /* user cancelled */
    } finally { setBusy(false); }
  };

  if (loading || !payload) return <div className="min-h-screen flex items-center justify-center bg-[#F7F3EA] text-[#6B7A6F]">Cargando…</div>;

  const pal = PALETTES[style];
  const fmt = formats[format] || { w: 1080, h: 1920, ratio: "9:16" };
  const ratio = fmt.h / fmt.w;
  const previewW = Math.min(360, typeof window !== "undefined" ? window.innerWidth - 48 : 360);
  const previewH = previewW * ratio;
  const data = buildData(payload, playerId || payload.default_player_id, contentType === "hole" ? hole : undefined);
  const showPosition = selectedTemplate && POSITION_TEMPLATES.includes(selectedTemplate.template_id);
  const holesPlayed = data.holes.filter((h) => h.score != null);

  return (
    <div className="min-h-screen bg-[#F7F3EA]" data-testid="social-studio-page">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#DED5C2] bg-white sticky top-0 z-10">
        <button onClick={() => navigate(-1)} className="text-[#6B7A6F]" data-testid="social-close"><X size={22} /></button>
        <span className="font-extrabold text-[#102E32]">Social Studio</span>
        <span className="w-6" />
      </div>

      <div className="max-w-md mx-auto px-4 pb-32">
        {/* Preview */}
        <div className="flex justify-center py-4">
          <div ref={canvasRef} style={{ width: previewW, height: previewH, borderRadius: 14, overflow: "hidden", position: "relative", background: mediaUrl ? "#000" : PALETTES[style].bg }} data-testid="social-preview">
            {mediaUrl && <img src={mediaUrl} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />}
            {mediaUrl && <div style={{ position: "absolute", inset: 0, background: pal.scrim }} />}
            {selectedTemplate && <TemplateOverlay id={selectedTemplate.template_id} W={previewW} H={previewH} data={data} pal={pal} position={position} mascot={mascot} mascotName={selectedTemplate.mascot} show={toggles} />}
          </div>
        </div>

        {!isFree && (
          <>
            <p className="text-xs font-bold text-[#6B7A6F] uppercase tracking-wide mt-2 mb-2">¿Qué quieres compartir?</p>
            <div className="flex flex-wrap gap-2">
              {availableTypes.map((t) => (
                <button key={t} data-testid={`social-content-type-${t}`} className={chipCls(contentType === t)} onClick={() => setContentType(t)}>{TYPE_META[t]}</button>
              ))}
            </div>
          </>
        )}

        {isFree && (
          <>
            <p className="text-xs font-bold text-[#6B7A6F] uppercase tracking-wide mt-2 mb-2">Personaliza tus datos</p>
            <div className="space-y-2">
              <div>
                <label className="text-[11px] font-bold text-[#6B7A6F]">Nombre</label>
                <input data-testid="social-free-name" value={freeForm.name} onChange={(e) => setFreeForm((f) => ({ ...f, name: e.target.value }))} placeholder="Tu nombre"
                  className="w-full h-11 bg-white border border-[#DED5C2] rounded-xl px-3 text-sm font-semibold text-[#102E32] focus:outline-none focus:ring-2 focus:ring-[#103B31] mt-1" />
              </div>
              <div>
                <label className="text-[11px] font-bold text-[#6B7A6F]">Campo / Evento</label>
                <input data-testid="social-free-course" value={freeForm.course} onChange={(e) => setFreeForm((f) => ({ ...f, course: e.target.value }))} placeholder="Ej. Club Campestre"
                  className="w-full h-11 bg-white border border-[#DED5C2] rounded-xl px-3 text-sm font-semibold text-[#102E32] focus:outline-none focus:ring-2 focus:ring-[#103B31] mt-1" />
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="text-[11px] font-bold text-[#6B7A6F]">Golpes</label>
                  <input data-testid="social-free-score" inputMode="numeric" value={freeForm.score} onChange={(e) => setFreeForm((f) => ({ ...f, score: e.target.value.replace(/[^0-9]/g, "") }))} placeholder="0"
                    className="w-full h-11 bg-white border border-[#DED5C2] rounded-xl px-3 text-sm font-semibold text-[#102E32] focus:outline-none focus:ring-2 focus:ring-[#103B31] mt-1" />
                </div>
                <div className="flex-1">
                  <label className="text-[11px] font-bold text-[#6B7A6F]">Vs Par (+/-)</label>
                  <input data-testid="social-free-topar" value={freeForm.toPar} onChange={(e) => setFreeForm((f) => ({ ...f, toPar: e.target.value.replace(/[^0-9-]/g, "") }))} placeholder="0"
                    className="w-full h-11 bg-white border border-[#DED5C2] rounded-xl px-3 text-sm font-semibold text-[#102E32] focus:outline-none focus:ring-2 focus:ring-[#103B31] mt-1" />
                </div>
              </div>
            </div>
          </>
        )}

        {payload.players.length > 1 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {payload.players.map((p) => (
              <button key={p.player_id} data-testid={`social-player-${p.player_id}`} className={chipCls((playerId || payload.default_player_id) === p.player_id)} onClick={() => { setPlayerId(p.player_id); setHole(undefined); }}>{p.name.split(" ")[0]}</button>
            ))}
          </div>
        )}

        {contentType === "hole" && (
          <>
            <p className="text-xs font-bold text-[#6B7A6F] uppercase tracking-wide mt-4 mb-2">Elige el hoyo</p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {holesPlayed.map((h) => (
                <button key={h.hole} data-testid={`social-hole-${h.hole}`} className={chipCls(hole === h.hole)} onClick={() => setHole(h.hole)}>H{h.hole}</button>
              ))}
            </div>
          </>
        )}

        <p className="text-xs font-bold text-[#6B7A6F] uppercase tracking-wide mt-4 mb-2">Elige tu estilo</p>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {typeTemplates.map((t) => (
            <button key={t.template_id} data-testid={`social-template-card-${t.template_id}`} onClick={() => setTemplateId(t.template_id)}
              className={`min-w-[120px] text-left rounded-2xl px-4 py-3 border-2 ${selectedTemplate?.template_id === t.template_id ? "border-[#103B31] bg-[#EEF6F0]" : "border-[#DED5C2] bg-white"}`}>
              <div className="font-extrabold text-[#102E32] text-sm">{t.name}</div>
              <div className="text-[#6B7A6F] text-xs">{t.subtitle}</div>
            </button>
          ))}
        </div>

        {selectedTemplate?.media && (
          <>
            <p className="text-xs font-bold text-[#6B7A6F] uppercase tracking-wide mt-4 mb-2">Foto</p>
            <div className="flex flex-wrap gap-2 items-center">
              <label className={chipCls(false) + " cursor-pointer flex items-center gap-1.5"} data-testid="social-media-upload">
                <ImageIcon size={15} /> Subir foto
                <input type="file" accept="image/*" className="hidden" onChange={onUpload} />
              </label>
              <button data-testid="social-media-none" className={chipCls(!mediaUrl)} onClick={() => setMediaUrl(null)}>Sin media</button>
              {mediaUrl && <span className="text-xs text-[#103B31] font-semibold">Foto cargada ✓</span>}
            </div>
            <p className="text-[11px] text-[#9AA69C] mt-1">El video está disponible en la app móvil de CADII.</p>
          </>
        )}

        <p className="text-xs font-bold text-[#6B7A6F] uppercase tracking-wide mt-4 mb-2">Formato</p>
        <div className="flex gap-2">
          {(selectedTemplate?.formats || []).map((f) => (
            <button key={f} data-testid={`social-format-${f}`} className={chipCls(format === f)} onClick={() => setFormat(f)}>{formats[f]?.ratio || f}</button>
          ))}
        </div>

        <p className="text-xs font-bold text-[#6B7A6F] uppercase tracking-wide mt-4 mb-2">Color</p>
        <div className="flex gap-2">
          {["cream", "green", "dark"].map((s) => (
            <button key={s} data-testid={`social-style-${s}`} className={chipCls(style === s)} onClick={() => setStyle(s)}>{STYLE_LABEL[s]}</button>
          ))}
        </div>

        {showPosition && (
          <>
            <p className="text-xs font-bold text-[#6B7A6F] uppercase tracking-wide mt-4 mb-2">Posición</p>
            <div className="flex gap-2">
              {["top", "center", "bottom"].map((p) => (
                <button key={p} data-testid={`social-position-${p}`} className={chipCls(position === p)} onClick={() => setPosition(p)}>{POS_LABEL[p]}</button>
              ))}
            </div>
          </>
        )}

        <p className="text-xs font-bold text-[#6B7A6F] uppercase tracking-wide mt-4 mb-2">Datos</p>
        <div className="flex flex-wrap gap-2">
          <button data-testid="social-mascot-toggle" className={chipCls(mascot)} onClick={() => setMascot((v) => !v)}>Mascota</button>
          <button data-testid="social-toggle-handicap" className={chipCls(toggles.handicap)} onClick={() => setToggles((t) => ({ ...t, handicap: !t.handicap }))}>HCP</button>
          <button data-testid="social-toggle-position" className={chipCls(toggles.position)} onClick={() => setToggles((t) => ({ ...t, position: !t.position }))}>Posición</button>
          <button data-testid="social-toggle-stats" className={chipCls(toggles.stats)} onClick={() => setToggles((t) => ({ ...t, stats: !t.stats }))}>Stats</button>
        </div>
      </div>

      <div className="fixed bottom-0 inset-x-0 border-t border-[#DED5C2] bg-[#F7F3EA] p-4">
        <div className="max-w-md mx-auto flex gap-3">
          <button data-testid="social-save-button" disabled={busy} onClick={download} className="flex-1 flex items-center justify-center gap-2 rounded-2xl py-4 bg-white border-2 border-[#103B31] text-[#103B31] font-extrabold disabled:opacity-60">
            <Download size={18} /> Descargar
          </button>
          <button data-testid="social-share-button" disabled={busy} onClick={share} className="flex-1 flex items-center justify-center gap-2 rounded-2xl py-4 bg-[#103B31] text-white font-extrabold disabled:opacity-60">
            <Share2 size={18} /> Compartir
          </button>
        </div>
      </div>
    </div>
  );
}

export function SocialCreateButton({ roundId, type = "round", template = "round_recap_01" }) {
  const navigate = useNavigate();
  return (
    <button
      data-testid="social-create-entry"
      onClick={() => navigate(`/social/${roundId}?type=${type}&template=${template}`)}
      className="w-full flex items-center justify-center gap-2 rounded-2xl py-3 bg-[#103B31] text-[#E8DFC8] font-extrabold"
    >
      <Sparkles size={16} /> Crear contenido
    </button>
  );
}
